/**
 * QuickBooks Desktop next to the stock — READ ONLY (owner decision 9 Oct 2026; API and part matching in
 * src/lib/qbDesktop.js). QuickBooks quantities and prices are shown beside the platform's own numbers;
 * nothing here changes a stock figure and nothing is written to QuickBooks.
 *
 * The Inventory page asks once per visit (useQbStock): the status, then — only when the integration is on
 * and has read QuickBooks at least once — the items. Off, not read yet or a failed request → null, and the
 * Stock tab's QuickBooks column, its filter chip and the "In QuickBooks" blocks are simply absent (no error
 * toast: it is extra information, the page works the same without it).
 */
import { useEffect, useState } from 'react';
import { BookCheck, TriangleAlert } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatDateTime, formatMoney, formatTimeAgo, toNumber } from '@/lib/format';
import { canAccessInvoicing } from '@/lib/permissions';
import { fetchQbItems, fetchQbStatus, itemsByPart, partKey, qbDataReady } from '@/lib/qbDesktop';
import { cn } from '@/lib/utils';
import { UpperLabel } from './InventoryUi';
import { formatQty, toQty } from './inventoryModel';

/** { byPart: Map(part key → QuickBooks item), readAt } once QuickBooks has been read; null otherwise. */
export function useQbStock() {
  const [qb, setQb] = useState(null);
  useEffect(() => {
    let ignore = false;
    const load = async () => {
      try {
        const status = await fetchQbStatus();
        if (ignore || !qbDataReady(status)) return;
        const data = await fetchQbItems();
        if (ignore) return;
        setQb({
          byPart: itemsByPart(Array.isArray(data?.items) ? data.items : []),
          readAt: data?.last_success_at || status.last_success_at
        });
      } catch (err) {
        // Silently absent (see above).
      }
    };
    load();
    return () => { ignore = true; };
  }, []);
  return qb;
}

/** The QuickBooks item of a platform part number — null when it is not in QuickBooks (or QuickBooks is not shown). */
export const qbItemOf = (qb, partNumber) => (qb && partNumber ? qb.byPart.get(partKey(partNumber)) || null : null);

/**
 * QuickBooks against the platform's own On hand → { qty, differs }; qty is null for an item QuickBooks keeps no
 * quantity of (service, non-inventory…). Like with like: QuickBooks' quantity on hand is one company-wide count —
 * every store, booked or not (it knows nothing of the platform's bookings) — so it is compared with On hand, the
 * part's stores added up with what is reserved still in them, never with Available. To the two decimals shown.
 */
export function qbStockCompare(qbItem, here) {
  const qty = toNumber(qbItem?.quantity_on_hand);
  if (qty === null) return { qty: null, differs: false };
  return { qty, differs: Math.round(qty * 100) !== Math.round(toQty(here) * 100) };
}

export const qbDiffTitle = (qty, here) => `QuickBooks ${formatQty(qty)} · here ${formatQty(here)}`;

const noQtyTitle = (qbItem) => `${qbItem.item_type || 'Item'} in QuickBooks — it keeps no quantity`;

/** Stock table cell: plain when QuickBooks agrees, amber when not, "—" when QuickBooks has no such item. */
export function QbQty({ qbItem, here, testId }) {
  if (!qbItem) return <span className="text-ink-4" title="Not in QuickBooks" aria-label="Not in QuickBooks" data-testid={testId}>—</span>;
  const { qty, differs } = qbStockCompare(qbItem, here);
  if (qty === null) return <span className="text-ink-4" title={noQtyTitle(qbItem)} aria-label={noQtyTitle(qbItem)} data-testid={testId}>—</span>;
  if (!differs) return <span className="text-ink-2" title="Same as QuickBooks" data-testid={testId}>{formatQty(qty)}</span>;
  return (
    <span className="inline-flex items-center gap-1 font-semibold text-status-amber-ink" title={qbDiffTitle(qty, here)} data-qb-differs="true" data-testid={testId}>
      <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />{formatQty(qty)}
    </span>
  );
}

/** Phone card: the same comparison as a short line (no hover on a phone, so the words say it). */
export function QbQtyLine({ qbItem, here, testId }) {
  const { qty, differs } = qbStockCompare(qbItem, here);
  let text = 'Not in QuickBooks';
  if (qbItem) text = qty === null ? `QuickBooks: ${qbItem.item_type || 'item'}, no quantity` : differs ? qbDiffTitle(qty, here) : `QuickBooks ${formatQty(qty)}`;
  return (
    <p className={cn('mt-1.5 flex items-center gap-1 text-[12px]', differs ? 'font-semibold text-status-amber-ink' : qbItem && qty !== null ? 'text-ink-2' : 'text-ink-3')} data-testid={testId}>
      {differs ? <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden="true" /> : <BookCheck className="h-3 w-3 shrink-0 text-ink-3" aria-hidden="true" />}
      <span className="truncate">{text}</span>
    </p>
  );
}

/** "QuickBooks quantities read 2 h ago" — above the Stock table / cards. */
export function QbReadLine({ readAt, differCount, className }) {
  return (
    <p className={cn('flex flex-wrap items-center gap-x-1.5 text-[12px] text-ink-3', className)} data-testid="stock-qb-read">
      <BookCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span title={formatDateTime(readAt)}>QuickBooks quantities read {formatTimeAgo(readAt)}</span>
      <span aria-hidden="true">·</span>
      <span>read only</span>
      {differCount ? (
        <>
          <span aria-hidden="true">·</span>
          <span className="font-semibold text-status-amber-ink">{differCount} {differCount === 1 ? 'line differs' : 'lines differ'}</span>
        </>
      ) : null}
    </p>
  );
}

function Fact({ label, wide, children }) {
  return (
    <div className={cn('min-w-0', wide && 'col-span-2')}>
      <dt className="text-ink-3">{label}</dt>
      <dd className="break-words font-semibold text-ink">{children}</dd>
    </div>
  );
}

/**
 * "In QuickBooks" — the matched item, read only: name, type, description, on hand (amber when it differs from
 * `here`, the platform's On hand), on order, sales price, average and purchase cost. Nothing when not matched.
 * `wide`: four columns from sm up (the Stock tab's expanded row); two otherwise (the Catalog drawer).
 * Prices and costs are money: only roles with Invoicing see them (owner rule 13 Oct 2026; the server leaves them
 * out of /qb-desktop/items for everyone else).
 */
export function QbItemFacts({ qbItem, here, readAt, wide = false, className, testId = 'qb-item-facts' }) {
  const { user } = useAuth() || {};
  const showMoney = canAccessInvoicing(user);
  if (!qbItem) return null;
  const { qty, differs } = qbStockCompare(qbItem, here);
  const type = [qbItem.item_type, qbItem.is_active === false ? 'inactive' : ''].filter(Boolean).join(' · ');
  return (
    <section className={cn('rounded-xl border border-line bg-surface-2 px-4 py-3', className)} aria-label="In QuickBooks" data-testid={testId}>
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <BookCheck className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />
        <UpperLabel>In QuickBooks</UpperLabel>
        <span className="text-[11.5px] text-ink-3" title={readAt ? formatDateTime(readAt) : undefined}>
          Read only{readAt ? ` · read ${formatTimeAgo(readAt)}` : ''}
        </span>
      </div>
      <dl className={cn('grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]', wide && 'sm:grid-cols-4')}>
        <Fact label="Name" wide><span className="font-mono text-[12px]">{qbItem.full_name || qbItem.name || '—'}</span></Fact>
        <Fact label="Description" wide>{qbItem.description || <span className="font-normal text-ink-3">No description</span>}</Fact>
        <Fact label="Type">{type || '—'}</Fact>
        <Fact label="On hand">
          {qty === null ? '—' : (
            <span className={cn('tabular-nums', differs && 'text-status-amber-ink')} title={differs ? qbDiffTitle(qty, here) : undefined} data-testid={`${testId}-on-hand`}>
              {formatQty(qty)}{differs ? <span className="font-normal"> · here {formatQty(here)}</span> : null}
            </span>
          )}
        </Fact>
        <Fact label="On order"><span className="tabular-nums">{qbItem.quantity_on_order === null || qbItem.quantity_on_order === undefined ? '—' : formatQty(qbItem.quantity_on_order)}</span></Fact>
        {showMoney ? (
          <>
            <Fact label="Sales price"><span className="tabular-nums">{formatMoney(qbItem.sales_price, { strict: true })}</span></Fact>
            <Fact label="Average cost"><span className="tabular-nums">{formatMoney(qbItem.average_cost, { strict: true })}</span></Fact>
            <Fact label="Purchase cost"><span className="tabular-nums">{formatMoney(qbItem.purchase_cost, { strict: true })}</span></Fact>
          </>
        ) : null}
      </dl>
    </section>
  );
}
